import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/format';
import { resolveImageUrl } from '@/components/ImageUploader';
import { Sparkles, ChevronLeft, ChevronRight, BadgeCheck } from 'lucide-react';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1200';
const ROTATE_MS = 4500;

/**
 * Auto-rotating carousel of sponsored products. Uses `/api/products/sponsored`
 * which falls back to newest products so the strip never renders empty. Users
 * can still nudge with arrow buttons — but the timer keeps advancing on its
 * own so it feels alive without user interaction.
 */
export const SponsoredCarousel = () => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [meta, setMeta] = useState({ is_fallback: false });
  const timerRef = useRef(null);

  useEffect(() => {
    api.get('/products/sponsored', { params: { limit: 8 } })
      .then(({ data }) => { setItems(data.items || []); setMeta({ is_fallback: !!data.is_fallback }); })
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % items.length), ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, items.length]);

  if (items.length === 0) return null;
  const clamp = (n) => (n + items.length) % items.length;
  const visible = [items[clamp(idx)], items[clamp(idx + 1)], items[clamp(idx + 2)]].filter(Boolean);

  return (
    <section
      data-testid="sponsored-carousel"
      className="border-b border-slate-200 bg-white"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-orange-600" />
            <h3 className="font-display font-bold text-slate-900 text-sm uppercase tracking-[0.2em]">
              🔥 Novedades destacadas
            </h3>
          </div>
          <div className="hidden sm:flex items-center gap-1">
            <button
              onClick={() => setIdx((i) => clamp(i - 1))}
              data-testid="carousel-prev"
              className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 hover:border-slate-900 hover:bg-slate-50"
              aria-label="Anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setIdx((i) => clamp(i + 1))}
              data-testid="carousel-next"
              className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 hover:border-slate-900 hover:bg-slate-50"
              aria-label="Siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
          {visible.map((p, i) => (
            <Link
              key={`${p.product_id}-${i}`}
              to={`/producto/${p.slug || p.product_id}`}
              data-testid={`sponsored-slot-${i}`}
              className={`group relative flex items-center gap-3 sm:gap-4 border border-slate-200 hover:border-orange-500 bg-white p-2.5 sm:p-3 transition-colors ${i > 0 ? 'hidden sm:flex' : ''} ${i > 1 ? 'sm:hidden md:flex' : ''}`}
            >
              <div className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 overflow-hidden bg-slate-50">
                <img
                  src={resolveImageUrl(p.images?.[0]) || PLACEHOLDER}
                  alt=""
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-600 flex items-center gap-1">
                  <BadgeCheck className="h-3 w-3" />
                  {meta.is_fallback ? p.brand : 'Destacado'}
                </div>
                <div className="font-display font-bold text-slate-900 text-sm mt-0.5 truncate group-hover:text-blue-700">
                  {p.name}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 font-mono truncate">Ref: {p.reference}</div>
                <div className="font-display font-black text-slate-900 text-base mt-1">
                  {formatCOP(p.price)}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Progress dots */}
        {items.length > 1 && (
          <div data-testid="carousel-dots" className="mt-3 flex items-center justify-center gap-1.5">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`carousel-dot-${i}`}
                className={`h-1 rounded-full transition-all ${
                  i === idx ? 'w-8 bg-orange-500' : 'w-1.5 bg-slate-300 hover:bg-slate-400'
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
